import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { EMPTY, forkJoin, Observable, switchMap } from 'rxjs';
import { escapeHtml } from '../../../../utils/html';
import { isHttpErrorResponse, UserService } from '@ipg/ngx-frontend-toolset';
import { BaseDialog } from '../../../../components/dialog/base-dialog';
import { DialogService } from '../../../../components/dialog/dialog.service';
import {
  MediaItem,
  MediaReferenceGroup,
  SharedMediaItem,
} from '../../../../models/media.model';
import { User } from '../../../../models/user.model';
import { ShareService } from '../../../../services/share.service';
import {
  MediaReferencesDialogComponent,
  ReferenceGroup,
} from '../media-references-dialog/media-references-dialog.component';

export interface MediaSettingsDialogData {
  media?: MediaItem;
  mediaItems?: MediaItem[];
}

@Component({
  selector: 'app-media-settings-dialog',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatSlideToggle,
  ],
  templateUrl: './media-settings-dialog.component.html',
  styleUrl: './media-settings-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaSettingsDialogComponent extends BaseDialog<MediaSettingsDialogData> {
  protected readonly dialogRef = inject(MatDialogRef<MediaSettingsDialogComponent>);
  private readonly _share = inject(ShareService);
  private readonly _dialog = inject(DialogService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _userService = inject<UserService<User>>(UserService);

  protected readonly items: MediaItem[] =
    this.data.mediaItems ?? (this.data.media ? [this.data.media] : []);
  protected readonly isBulk = this.items.length > 1;

  protected readonly isShared = signal<boolean>(
    this.isBulk ? this.items.every((m) => !!m.shareId) : !!this.items[0]?.shareId
  );
  protected readonly isSaving = signal<boolean>(false);

  protected readonly itemCount = this.items.length;

  protected readonly hasExternalItems = this.items.some(
    (m) => 'isOwn' in m && !(m as SharedMediaItem).isOwn
  );

  protected toggleShare(enabled: boolean): void {
    this.isShared.set(enabled);
  }

  protected save(): void {
    if (this.isSaving()) return;

    const willShare = this.isShared();

    this.isSaving.set(true);

    if (this.isBulk) {
      this._saveBulk(willShare);
    } else {
      this._saveSingle(willShare);
    }
  }

  private _saveBulk(willShare: boolean): void {
    if (willShare) {
      const unsharedIds = this.items.filter((m) => !m.shareId).map((m) => m.id);
      if (!unsharedIds.length) {
        this.dialogRef.close();
        return;
      }

      this._share
        .bulkShare(unsharedIds)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: () => {
            this.isSaving.set(false);
            this.dialogRef.close({ shared: true });
          },
          error: () => {
            this.isSaving.set(false);
            this._dialog.alert({
              title: 'Foutmelding',
              message:
                'Er is iets misgegaan bij het delen van de media. Probeer het later nog eens.',
              confirmText: 'Begrepen',
            });
          },
        });
    } else {
      const sharedItems = this.items.filter((m) => !!m.shareId);
      if (!sharedItems.length) {
        this.dialogRef.close();
        return;
      }

      // Pre-check: fetch references for all shared items
      const refRequests = sharedItems.reduce(
        (acc, item) => {
          acc[item.id] = this._share.getReferences(item.id);
          return acc;
        },
        {} as Record<string, Observable<ReferenceGroup[]>>
      );

      forkJoin(refRequests)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (allRefs) => {
            const mediaGroups: MediaReferenceGroup[] = [];

            for (const item of sharedItems) {
              const groups = allRefs[item.id] ?? [];
              const currentUnitId = this._userService.data()?.unitId;
              const crossUnitGroups = groups.filter(
                (group) => group.unitId !== null && group.unitId !== currentUnitId
              );

              if (crossUnitGroups.length > 0) {
                const totalCount = crossUnitGroups.reduce(
                  (sum, g) => sum + g.totalCount,
                  0
                );

                mediaGroups.push({
                  mediaLabel: item.label,
                  references: crossUnitGroups.flatMap((g) => g.references),
                  totalCount,
                });
              }
            }

            if (mediaGroups.length > 0) {
              this.isSaving.set(false);
              this.dialogRef.close();
              this._dialog.open(MediaReferencesDialogComponent, {
                mediaGroups,
                mode: 'unshare',
              });
              return;
            }

            // No cross-unit refs — proceed with bulk unshare
            this._share
              .bulkUnshare(sharedItems.map((m) => m.id))
              .pipe(takeUntilDestroyed(this._destroyRef))
              .subscribe({
                next: () => {
                  this.isSaving.set(false);
                  this.dialogRef.close({ shared: false });
                },
                error: (error) => {
                  this.isSaving.set(false);
                  this._showUnshareError(error);
                },
              });
          },
          error: () => {
            this.isSaving.set(false);
            this._dialog.alert({
              title: 'Foutmelding',
              message:
                'Er is iets misgegaan bij het controleren van de media referenties. Probeer het later nog eens.',
              confirmText: 'Begrepen',
            });
          },
        });
    }
  }

  private _saveSingle(willShare: boolean): void {
    const media = this.items[0];
    const wasShared = !!media.shareId;

    if (wasShared === willShare) {
      this.dialogRef.close();
      return;
    }

    if (willShare) {
      this._share
        .share(media.id)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: () => {
            this.isSaving.set(false);
            this.dialogRef.close({ shared: true });
          },
          error: () => {
            this.isSaving.set(false);
            this._dialog.alert({
              title: 'Foutmelding',
              message:
                'Er is iets misgegaan bij het delen van de media. Probeer het later nog eens.',
              confirmText: 'Begrepen',
            });
          },
        });
    } else {
      this._share
        .getReferences(media.id)
        .pipe(
          takeUntilDestroyed(this._destroyRef),
          switchMap((groups) => {
            const currentUnitId = this._userService.data()?.unitId;
            const crossUnitGroups = groups.filter(
              (group) => group.unitId !== null && group.unitId !== currentUnitId
            );

            if (crossUnitGroups.length > 0) {
              this.isSaving.set(false);
              this.dialogRef.close();
              this._dialog.open(MediaReferencesDialogComponent, {
                referenceGroups: groups,
                mode: 'unshare',
              });
              return EMPTY;
            }

            return this._share.unshare(media.id);
          })
        )
        .subscribe({
          next: () => {
            this.isSaving.set(false);
            this.dialogRef.close({ shared: false });
          },
          error: (error) => {
            this.isSaving.set(false);
            this._showUnshareError(error);
          },
        });
    }
  }

  private _showUnshareError(error: unknown): void {
    if (
      isHttpErrorResponse(error) &&
      error.status === 409 &&
      error.error.detail.errorCode === 'MEDIA_USED_IN_SHARED_TEMPLATE'
    ) {
      const references = (error.error.detail.metadata?.['references'] ?? []) as {
        label: string;
      }[];
      const templateNames = references.map((r) => r.label).join(', ');

      this._dialog.alert({
        title: 'Media kan niet worden gestopt met delen',
        message: `Deze media wordt gebruikt in een gedeelde template (${escapeHtml(templateNames)}). Stop eerst met delen van de template voordat je de media kunt stoppen met delen.`,
        confirmText: 'Begrepen',
      });
    } else {
      this._dialog.alert({
        title: 'Foutmelding',
        message:
          'Er is iets misgegaan bij het stoppen met delen van de media. Probeer het later nog eens.',
        confirmText: 'Begrepen',
      });
    }
  }
}
